import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import {
  AccountStatementApiFactory,
  AccountStatementFormat,
  type AccountStatementSummary,
  type GenerateAccountStatementRequest,
} from '@mynt/backoffice-api-spec/account-statement'
import Axios from 'api/axios'
import config from 'config'
import dayjs, { type Dayjs } from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CoreMyntModal } from '../../components/modals/CoreMyntModal.tsx'
import useMaterialNotification from '../../hooks/useMaterialNotification.tsx'
import Tabs from '../../mynt-components/components/Tabs.tsx'

const accountStatementApi = AccountStatementApiFactory(undefined, config.boBaseUrl, Axios)

interface AccountStatementDialogProps {
  open: boolean
  onClose: () => void
  customerId: string
  accountId: string
  accountName: string
}

export const AccountStatementDialog = ({ open, onClose, customerId, accountId, accountName }: AccountStatementDialogProps) => {
  const notify = useMaterialNotification()
  const notifyRef = useRef(notify)
  notifyRef.current = notify
  const [activeTab, setActiveTab] = useState(0)
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().subtract(1, 'month').startOf('month'),
    dayjs().subtract(1, 'month').endOf('month'),
  ])
  const [format, setFormat] = useState<AccountStatementFormat>(AccountStatementFormat.PDF)
  const [generating, setGenerating] = useState(false)
  const generatingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(generatingTimeoutRef.current), [])
  const [statements, setStatements] = useState<AccountStatementSummary[]>([])
  const [loadingStatements, setLoadingStatements] = useState(false)

  const fetchStatements = useCallback(async () => {
    setLoadingStatements(true)
    try {
      const { data } = await accountStatementApi.listAccountStatements(customerId, accountId)
      setStatements(data.statements.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()))
    } catch {
      notifyRef.current('Failed to load statements', 'error')
    } finally {
      setLoadingStatements(false)
    }
  }, [customerId, accountId])

  const hasPending = statements.some((s) => s.status === 'PENDING')

  useEffect(() => {
    if (open && activeTab === 1) fetchStatements()
  }, [open, activeTab, fetchStatements])

  useEffect(() => {
    if (!open || activeTab !== 1 || !hasPending) return
    const interval = setInterval(fetchStatements, 3000)
    return () => clearInterval(interval)
  }, [open, activeTab, hasPending, fetchStatements])

  const [fromDate, toDate] = dateRange

  const handleGenerate = async () => {
    if (!fromDate || !toDate) {
      notify('Please select both from and to dates', 'error')
      return
    }

    const request: GenerateAccountStatementRequest = {
      accountId,
      from: fromDate.format('YYYY-MM-DD'),
      to: toDate.format('YYYY-MM-DD'),
      format,
    }

    setGenerating(true)

    try {
      const { data: generateResponse } = await accountStatementApi.generateAccountStatement(customerId, request)

      if (generateResponse.type === 'FAILED') {
        notify('Failed to generate account statement', 'error')
        return
      }

      notify('Account statement generation started')
    } catch {
      notify('An error occurred while generating the account statement', 'error')
    } finally {
      generatingTimeoutRef.current = setTimeout(() => setGenerating(false), 1000)
    }
  }

  const handleDownload = async (statement: AccountStatementSummary) => {
    try {
      const { data: response } = await accountStatementApi.fetchAccountStatement(customerId, statement.id)
      if (response.type === 'READY' && response.url) {
        window.open(response.url, '_blank')
      } else {
        notify(`Statement is not ready (${response.type})`, 'error')
      }
    } catch {
      notify('Failed to download statement', 'error')
    }
  }

  return (
    <CoreMyntModal
      title="Account Statement"
      open={open}
      onClose={onClose}
      wrapperProps={{ sx: { width: 700 } }}
      actions={(Wrapper) =>
        activeTab === 0 ? (
          <Wrapper>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleGenerate} disabled={!accountId || generating}>
              Generate
            </Button>
          </Wrapper>
        ) : (
          <Wrapper>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          </Wrapper>
        )
      }
    >
      <Tabs
        tabKeyNameSpace="account-statement"
        tab={activeTab}
        onTabChange={setActiveTab}
        alwaysShowNavHeadings
        tabs={[
          {
            id: 'generate',
            labelElement: <Typography variant="body2">New statement</Typography>,
            contentElement: (
              <Stack spacing={3}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Generate a new statement for <strong>{accountName}</strong> for the selected period.
                </Typography>
                <DateRangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  maxDate={dayjs().subtract(1, 'day')}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: { size: 'small' },
                  }}
                />
                <RadioGroup row value={format} onChange={(e) => setFormat(e.target.value as AccountStatementFormat)}>
                  <FormControlLabel value={AccountStatementFormat.PDF} control={<Radio size="small" />} label="PDF" />
                  <FormControlLabel value={AccountStatementFormat.EXCEL} control={<Radio size="small" />} label="Excel" />
                </RadioGroup>
              </Stack>
            ),
          },
          {
            id: 'generated-statements',
            labelElement: <Typography variant="body2">Generated statements</Typography>,
            contentElement: (
              <Stack spacing={2} sx={{ maxHeight: 500, overflowY: 'auto' }}>
                {loadingStatements && <Typography variant="body2">Loading...</Typography>}
                {!loadingStatements && statements.length === 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    No statements generated yet.
                  </Typography>
                )}
                {statements.map((statement) => {
                  return (
                    <Box
                      key={statement.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          sx={{
                            minWidth: 50,
                            color: 'text.secondary',
                            flexShrink: 0,
                          }}
                        >
                          {statement.format}
                        </Typography>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {statement.fileName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              display: 'block',
                            }}
                          >
                            {dayjs(statement.createdAt).format('YYYY-MM-DD HH:mm')}
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                              display: 'block',

                              color:
                                statement.status === 'COMPLETED'
                                  ? 'success.main'
                                  : statement.status === 'FAILED'
                                    ? 'error.main'
                                    : 'text.secondary',
                            }}
                          >
                            {statement.status === 'COMPLETED' ? 'Ready' : statement.status === 'FAILED' ? 'Failed' : 'Pending'}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={statement.status !== 'COMPLETED'}
                        onClick={() => handleDownload(statement)}
                      >
                        Download
                      </Button>
                    </Box>
                  )
                })}
              </Stack>
            ),
          },
        ]}
      />
    </CoreMyntModal>
  )
}
